// Third party library imports
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import {
    APP_INITIALIZER,
    ApplicationConfig,
    ErrorHandler,
    importProvidersFrom,
    inject,
    provideAppInitializer,
    Signal,
} from '@angular/core';
import { includeBearerTokenInterceptor, KEYCLOAK_EVENT_SIGNAL, KeycloakEvent, KeycloakEventType } from 'keycloak-angular';
import { withAutoRefreshToken } from 'keycloak-angular';
import { getFirestore, provideFirestore } from '@angular/fire/firestore';
import { provideAuth, initializeAuth, browserSessionPersistence } from '@angular/fire/auth';
import { initializeApp, provideFirebaseApp } from '@angular/fire/app';
import { GoogleMapsModule } from '@angular/google-maps';

import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { LuxonDateAdapter } from '@angular/material-luxon-adapter';
import { DateAdapter, MAT_DATE_FORMATS, MatNativeDateModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatStepperModule } from '@angular/material/stepper';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import {
    PreloadAllModules,
    provideRouter,
    Router,
    withInMemoryScrolling,
    withPreloading,
} from '@angular/router';
import * as Sentry from '@sentry/angular';
import { provideTransloco, TranslocoService } from '@ngneat/transloco';
import { FormlyModule } from '@ngx-formly/core';
import { FormlyPresetModule } from '@ngx-formly/core/preset';
import { FormlyMaterialModule } from '@ngx-formly/material';
import { FormlyMatDatepickerModule } from '@ngx-formly/material/datepicker';
import { provideLottieOptions } from 'ngx-lottie';
import { provideEventPlugins } from '@taiga-ui/event-plugins';

import { providePrimeNG } from 'primeng/config';
import Material from '@primeng/themes/material';

// Zopup library imports
import { provideZopAuth, provideZopKeycloakAuth, provideZopAuthConfig } from '@zopup/zop-auth';
import { provideZopCore } from '@zopup/zop-core';
import { provideZopApis, UOMRepositoryStore, UomPreferenceStore } from '@zopup/zop-apis';

import {
    invalidDateRangeValidator,
    invalidOdometerValueValidator,
    leafNodeValidator,
    toFromDateValidator,
    yearDifferenceValidator,
    globalErrorInterceptor,
    StorageService,
    StorageKey,
} from '@zopup/zop-shared';
import { TenantDto } from '@zopup/zop-types';
import { provideZopBranding, provideZopManufacturingRoutes, provideZopUxCore, zopLoadingInterceptor } from '@zopup/zop-ux-core';
import { provideZopUxLayoutCore } from '@zopup/zop-ux-layout';

// Internal project imports
import { environment } from '../environment/environment';
import { appRoutes } from './app.routes';
import {
    maxLengthValidationMessage,
    maxValidationMessage,
    minLengthValidationMessage,
    minValidationMessage,
    types,
} from './formly.config';
import { mockApiServices } from './mock-api/mock-service.const';
import { provideIcons } from './providers/icons/icons.provider';
import { provideZopI18n, preloadTranslations } from '@zopup/zop-i18n';
import { MAIN_APP_AUTH_CONFIG } from './config/zop-auth.config';
import { INFLEWZ_MAIN_APP_BRANDING_CONFIG } from './config/branding.config';

const fbApp = () => initializeApp(environment.firebase);
const authApp = () => initializeAuth(fbApp(), {
    persistence: browserSessionPersistence,
});
const fbDb = () => getFirestore(fbApp());
const firebaseProviders = [
    provideFirebaseApp(fbApp),
    provideAuth(authApp),
    provideFirestore(fbDb),
];

/**
 * Resolves once Keycloak has finished its own init() flow (success or not).
 * `keycloak.authenticated` is `false` from the moment the Keycloak instance is
 * constructed (not `undefined`), so polling it can't distinguish "not ready yet"
 * from "checked and unauthenticated" — it resolves instantly and doesn't wait at
 * all. `KEYCLOAK_EVENT_SIGNAL` only flips away from `KeycloakAngularInit` once
 * `keycloak.init()` actually settles (see `keycloak.onReady` in keycloak-js),
 * so that's the correct readiness signal. Without waiting for it, this
 * APP_INITIALIZER can race ahead of Keycloak's async init and fire authenticated
 * HTTP calls before a token exists, causing spurious 401s (e.g. GET
 * /UOM?IsActive=true on startup).
 */
function waitForKeycloakReady(keycloakSignal: Signal<KeycloakEvent>, timeoutMs = 10000): Promise<void> {
    return new Promise((resolve) => {
        const start = Date.now();
        const check = () => {
            if (keycloakSignal().type !== KeycloakEventType.KeycloakAngularInit || Date.now() - start > timeoutMs) {
                resolve();
                return;
            }
            setTimeout(check, 50);
        };
        check();
    });
}

/**
 * Initialize UOM system on application startup
 * - Loads all active UOMs into cache
 * - Loads tenant preferences and region profiles if tenant is available
 */
function initializeUomSystem(
    uomStore: InstanceType<typeof UOMRepositoryStore>,
    prefStore: InstanceType<typeof UomPreferenceStore>,
    storage: StorageService,
    keycloakSignal: Signal<KeycloakEvent>
) {
    return async () => {
        try {
            console.log('[UOM System] Initializing...');

            await waitForKeycloakReady(keycloakSignal);

            // Check if tenant is available first
            const tenant = storage.getItem<TenantDto>(StorageKey.tenant);
            if (!tenant?.id) {
                console.log('[UOM System] No tenant available, skipping initialization');
                return;
            }

            // Load all active UOMs (small dataset ~100-200 records)
            await uomStore.fetchPage({ isActive: true });
            console.log('[UOM System] UOMs loaded:', Object.keys(uomStore.entities()).length);

            // Load tenant preferences
            await prefStore.initialize();
            console.log('[UOM System] Preferences loaded for tenant:', tenant.id);

            console.log('[UOM System] Initialization complete');
        } catch (error) {
            console.error('[UOM System] Failed to initialize:', error);
            // Don't block app startup on UOM initialization failure
        }
    };
}

export const appConfig: ApplicationConfig = {
    providers: [
        {
            provide: ErrorHandler,
            useValue: Sentry.createErrorHandler(),
        },
        {
            provide: Sentry.TraceService,
            deps: [Router],
        },
        {
            provide: APP_INITIALIZER,
            useFactory: () => () => {},
            deps: [Sentry.TraceService],
            multi: true,
        },
        provideAnimations(),
        provideAnimationsAsync(),
        // Dotted event modifiers (e.g. ng-draw-flow's `pointerdown.stop` connector listener)
        // only bind when Taiga's event-manager plugins are registered.
        provideEventPlugins(),
        // Consolidated HTTP client with all interceptors
        // provideHttpClient(withInterceptors([
        //     zopLoadingInterceptor,          // Loading indicator interceptor
        // ])),
        provideRouter(
            appRoutes,
            withPreloading(PreloadAllModules),
            withInMemoryScrolling({ scrollPositionRestoration: 'enabled' })
        ),

        // Angular fire base provider
        importProvidersFrom(
            GoogleMapsModule,
            FormlyModule.forRoot({
                types: types,
                validators: [
                    {
                        name: 'toFromDateValidator',
                        validation: toFromDateValidator,
                    },
                    {
                        name: 'yearDifferenceValidator',
                        validation: yearDifferenceValidator,
                    },
                    { name: 'isLeafNode', validation: leafNodeValidator },
                    {
                        name: 'invalidOdometerValue',
                        validation: invalidOdometerValueValidator,
                    },
                    {
                        name: 'invalidDateRange',
                        validation: invalidDateRangeValidator,
                    },
                ],
                validationMessages: [
                    { name: 'required', message: 'This field is required' },
                    { name: 'minLength', message: minLengthValidationMessage },
                    { name: 'maxLength', message: maxLengthValidationMessage },
                    { name: 'min', message: minValidationMessage },
                    { name: 'max', message: maxValidationMessage },
                    {
                        name: 'toFromDateValidator',
                        message: 'To Date must be later than From Date',
                    },
                    {
                        name: 'yearDifferenceValidator',
                        message: 'Invalid Odometer Value',
                    },
                    {
                        name: 'invalidOdometerValue',
                        message: 'Invalid Odometer Value',
                    },
                    {
                        name: 'invalidDateRange',
                        message: 'Start Date must be before End Date',
                    },
                ],
            }),
            FormsModule,
            ReactiveFormsModule,
            FormlyMaterialModule,
            FormlyMatDatepickerModule,
            MatStepperModule,
            MatDatepickerModule,
            MatNativeDateModule,
            FormlyPresetModule
        ),

        ...firebaseProviders,

        // Material Date Adapter
        {
            provide: DateAdapter,
            useClass: LuxonDateAdapter,
        },
        {
            provide: MAT_DATE_FORMATS,
            useValue: {
                parse: {
                    dateInput: 'D',
                },
                display: {
                    dateInput: 'DDD',
                    monthYearLabel: 'LLL yyyy',
                    dateA11yLabel: 'DD',
                    monthYearA11yLabel: 'LLLL yyyy',
                },
            },
        },

        // Transloco Config - Shared i18n with merge loader and INFLEWZ overrides
        provideZopI18n({
            appNamespace: 'inflewz',
            prodMode: true,
        }),
        provideAppInitializer(preloadTranslations()),

        // Zop Providers
        provideZopAuth(MAIN_APP_AUTH_CONFIG),
        provideZopApis(environment),

        // UOM System Initializer
        {
            provide: APP_INITIALIZER,
            useFactory: initializeUomSystem,
            deps: [UOMRepositoryStore, UomPreferenceStore, StorageService, KEYCLOAK_EVENT_SIGNAL],
            multi: true,
        },

        provideIcons(),
        provideZopCore({
            mockApi: {
                delay: 0,
                services: mockApiServices,
            },
            httpInterceptors: [
                globalErrorInterceptor,         // Global HTTP error handling interceptor
                zopLoadingInterceptor,          // Loading indicator interceptor
                includeBearerTokenInterceptor,   // Keycloak Bearer token interceptor
            ],
        }),
        provideZopUxCore(),
        provideZopBranding(INFLEWZ_MAIN_APP_BRANDING_CONFIG),
        // inflewz-main-app nests manufacturing routes under `work-management` (see app.routes.ts)
        // rather than mounting them at the top level — override the default base path so
        // zop-ux-project's material requirement deep links resolve correctly here.
        provideZopManufacturingRoutes({ materialRequirementsBasePath: '/work-management/material-requirements' }),
        provideZopUxLayoutCore({
            layout: 'dense',
            scheme: 'light',
            screens: {
                sm: '600px',
                md: '960px',
                lg: '1280px',
                xl: '1440px',
            },
            theme: 'theme-teal',
            themes: [
                {
                    id: 'theme-default',
                    name: 'Default',
                },
                {
                    id: 'theme-brand',
                    name: 'Brand',
                },
                {
                    id: 'theme-teal',
                    name: 'Teal',
                },
                {
                    id: 'theme-rose',
                    name: 'Rose',
                },
                {
                    id: 'theme-purple',
                    name: 'Purple',
                },
                {
                    id: 'theme-amber',
                    name: 'Amber',
                },
            ],
            navigationUrl: 'api/common/navigation',
            isProduction: false,
            hideFooter: true,
            hideHeader: true,
            copyWriteText: 'eValuz',
        }),
        provideLottieOptions({
            player: () => import('lottie-web'),
        }),

        // Prime Ng
        providePrimeNG({
            theme: {
                preset: Material,
                options: {
                    cssLayer: {
                        name: 'primeng',
                        order: 'tailwind-base, primeng, tailwind-utilities',
                    },
                    darkModeSelector: false, // Class to trigger dark mode
                },
            },
        }),

        // Keycloak (modern setup) - HTTP client provided by provideZopCore
        ...provideZopKeycloakAuth({
            config: environment.keycloak, // { url, realm, clientId }
            excludeHttpClient: true, // HTTP client is provided by provideZopCore with all interceptors
            initOptions: {
                onLoad: 'login-required',
                // NOTE: silentCheckSsoRedirectUri must NOT be set when using onLoad: 'login-required'.
                // Silent SSO check is only for the 'check-sso' flow. With 'login-required', Keycloak
                // already redirects to authenticate, so the silent iframe is redundant. Setting it
                // causes Keycloak to attempt an XHR token exchange from the iframe which is blocked
                // by CORS (localhost not whitelisted in Keycloak) and fails initialisation entirely.
                enableLogging: true,
                checkLoginIframe: false,
                useNonce: true,
                flow: 'standard',
                checkLoginIframeInterval: 0,
                pkceMethod: 'S256',
            },
            features: environment.production ? [
                withAutoRefreshToken({
                    onInactivityTimeout: 'logout',
                    sessionTimeout: 900000, // 15 minutes
                }),
            ] : [],
        }),
    ],
};
